import axios from 'axios';
import config from '../../constants/apiConfig';
import {
  ICS_FORM_FAMILY_LABELS,
  isFormImplementedForFamily,
  resolveFormName,
  resolveIncidentFormFamily,
  toBaseFormId,
} from 'components/IAP/formFamily';

type Base64PdfString = string;

export type ExportAsFormParams = {
  formname: string;
  data?: any;
  mapSketch: any;
  periodNumber?: number;
  /** Pdf (default), docx, or xlsx. Pdf and Word open in the preview dialog. */
  format?: 'pdf' | 'docx' | 'xlsx';
};

export type ExportAsFormDeps = {
  incident: unknown;
  user: unknown;
  groupGuid: string;
};

/**
 * POST exportAsForm — used by IcsFormPdfPreviewProvider for preview + optional
 * download.
 */
export type PersistIcsFormExportPayload = {
  pdfBase64: string;
  workspaceFormname: string;
  periodNumber?: number | null;
  /** Decides the archived file's extension and content type. */
  format?: 'pdf' | 'docx' | 'xlsx';
};

export async function postPersistIcsFormExport(
  deps: ExportAsFormDeps,
  payload: PersistIcsFormExportPayload
): Promise<void> {
  const formData = new FormData();
  formData.append('pdfBase64', payload.pdfBase64);
  formData.append('formname', payload.workspaceFormname);
  formData.append('incident', JSON.stringify(deps.incident));
  formData.append('format', payload.format ?? 'pdf');
  if (payload.periodNumber != null) {
    formData.append('periodNumber', String(payload.periodNumber));
  }

  await axios.post(config.apiGateway.persistIcsFormExport, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function postExportAsForm(
  deps: ExportAsFormDeps,
  {
    formname,
    data = {},
    mapSketch,
    periodNumber,
    format = 'pdf',
  }: ExportAsFormParams
): Promise<{
  formname: string;
  data: string;
  format: 'pdf' | 'docx' | 'xlsx';
}> {
  // Callers name Coast Guard forms; the incident's family decides which
  // authority's template is actually rendered.
  const family = resolveIncidentFormFamily(deps.incident);
  const baseId = toBaseFormId(formname);
  if (!isFormImplementedForFamily(baseId, family)) {
    throw new Error(
      `${baseId.replace('_', ' ')} is not available for ${ICS_FORM_FAMILY_LABELS[family]} incidents.`
    );
  }
  const resolvedFormname = resolveFormName(baseId, family);

  const formData = new FormData();
  if (mapSketch !== undefined && mapSketch !== null) {
    formData.append('file', mapSketch);
  }
  formData.append('data', JSON.stringify(data));
  formData.append('formname', resolvedFormname);
  formData.append('incident', JSON.stringify(deps.incident));
  formData.append('user', JSON.stringify(deps.user));
  formData.append('group_guid', deps.groupGuid);
  formData.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
  formData.append('format', format);
  if (periodNumber != null) {
    formData.append('periodNumber', String(periodNumber));
  }

  const response = await axios.post<Base64PdfString>(
    config.apiGateway.exportAsForm,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return { formname: resolvedFormname, data: response.data || '', format };
}
